import { QueryClient, MutationCache, QueryCache } from "@tanstack/react-query";
import { isApiError } from "@/types/api.types";
import { notifyError } from "@/lib/notify";

export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 2, // 2 minutes
        gcTime: 1000 * 60 * 10, // 10 minutes (garbage collection)
        retry: (failureCount, error) => {
          if (isApiError(error)) {
            // Don't retry auth errors or not found
            if (error.type === "authentication" || error.type === "authorization") {
              return false;
            }
            if (error.type === "not_found" || error.type === "validation") {
              return false;
            }
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Log query errors in development
        if (import.meta.env.DEV) {
          console.error(`Query error [${String(query.queryKey)}]:`, error);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (isApiError(error) && error.type === "authentication") {
          // Auth errors are handled by apiClient (token refresh / redirect)
          return;
        }
        // Log mutation errors in development
        if (import.meta.env.DEV) {
          console.error("Mutation error:", error);
        }
        // Surface a clear error toast for every mutation across the app, unless the
        // mutation opts out (e.g. it renders its own inline error or custom toast).
        if (mutation.meta?.suppressErrorToast) {
          return;
        }
        notifyError(error, { title: mutation.meta?.errorTitle });
      },
    }),
  });
};
